import React, { useState } from 'react';
import {
  MessageSquare, Search, Plus, Send, Paperclip, Smile, Phone, Video,
  MoreVertical, Star, Clock, CheckCircle, AlertCircle, User, Calendar,
  FileText, Image, Download, Edit, Trash2, Pin, Archive, Settings,
  Users, Briefcase, Heart, Shield, Award, Zap, Filter
} from 'lucide-react';

const Messages = ({ company }) => {
  const [selectedConversation, setSelectedConversation] = useState(1);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data pour les conversations
  const conversations = [
    {
      id: 1,
      freelancer: {
        id: 1,
        name: "Camille Dupont",
        avatar: "CD",
        title: "Designer UX/UI Éco-responsable",
        status: "online",
        lastSeen: "En ligne",
        gradient: "from-blue-500 to-cyan-500"
      },
      project: {
        title: "Refonte UX/UI App Mobile",
        status: "active"
      },
      lastMessage: {
        content: "Parfait ! J'ai terminé les wireframes. Quand peut-on prévoir une session de review ?",
        timestamp: "2024-01-15T14:30:00",
        sender: "freelancer",
        type: "text"
      },
      unreadCount: 2,
      pinned: true,
      messages: [
        {
          id: 1,
          content: "Bonjour ! Merci d'avoir accepté ma candidature. J'ai hâte de commencer à travailler sur ce projet d'app mobile éco-responsable.",
          timestamp: "2024-01-15T09:00:00",
          sender: "freelancer",
          type: "text"
        },
        {
          id: 2,
          content: "Bonjour Camille ! Nous sommes ravis de collaborer avec vous. Votre approche éco-design correspond parfaitement à nos valeurs.",
          timestamp: "2024-01-15T09:15:00",
          sender: "company",
          type: "text"
        },
        {
          id: 3,
          content: "Voici le brief détaillé du projet",
          timestamp: "2024-01-15T09:16:00",
          sender: "company",
          type: "file",
          fileName: "Brief_App_Mobile_EcoTech.pdf",
          fileSize: "2.4 MB"
        },
        {
          id: 4,
          content: "Merci ! J'ai bien reçu le brief. Je vais commencer par une phase de recherche utilisateur axée sur les comportements éco-responsables. Je vous propose un premier point dans 48h ?",
          timestamp: "2024-01-15T10:30:00",
          sender: "freelancer",
          type: "text"
        },
        {
          id: 5,
          content: "Parfait ! Nous apprécions votre approche méthodique. Planifions un call pour mercredi 14h ?",
          timestamp: "2024-01-15T11:00:00",
          sender: "company",
          type: "text"
        },
        {
          id: 6,
          content: "Parfait ! J'ai terminé les wireframes. Quand peut-on prévoir une session de review ?",
          timestamp: "2024-01-15T14:30:00",
          sender: "freelancer",
          type: "text"
        }
      ]
    },
    {
      id: 2,
      freelancer: {
        id: 2,
        name: "Thomas Bernard",
        avatar: "TB",
        title: "Développeur Full-Stack",
        status: "offline",
        lastSeen: "Il y a 2h",
        gradient: "from-green-500 to-emerald-500"
      },
      project: {
        title: "Dashboard Analytics RSE",
        status: "completed"
      },
      lastMessage: {
        content: "Le dashboard est en ligne ! Les métriques RSE s'affichent correctement. Merci pour cette super collaboration 🎉",
        timestamp: "2024-01-14T16:45:00",
        sender: "freelancer",
        type: "text"
      },
      unreadCount: 0,
      pinned: false,
      messages: [
        {
          id: 1,
          content: "Salut ! Le développement du dashboard avance bien. J'ai intégré les APIs de suivi carbone.",
          timestamp: "2024-01-14T10:00:00",
          sender: "freelancer",
          type: "text"
        },
        {
          id: 2,
          content: "Excellent ! Pouvez-vous me montrer un aperçu ?",
          timestamp: "2024-01-14T10:15:00",
          sender: "company",
          type: "text"
        },
        {
          id: 3,
          content: "dashboard-preview.png",
          timestamp: "2024-01-14T10:20:00",
          sender: "freelancer",
          type: "image",
          fileName: "dashboard-preview.png"
        },
        {
          id: 4,
          content: "Le dashboard est en ligne ! Les métriques RSE s'affichent correctement. Merci pour cette super collaboration 🎉",
          timestamp: "2024-01-14T16:45:00",
          sender: "freelancer",
          type: "text"
        }
      ]
    },
    {
      id: 3,
      freelancer: {
        id: 3,
        name: "Sophie Martin",
        avatar: "SM",
        title: "Data Scientist",
        status: "away",
        lastSeen: "Il y a 30min",
        gradient: "from-purple-500 to-pink-500"
      },
      project: {
        title: "Analyse Impact Carbone",
        status: "pending"
      },
      lastMessage: {
        content: "Bonjour ! J'ai quelques questions sur les données à analyser. Pouvez-vous m'éclairer ?",
        timestamp: "2024-01-13T14:20:00",
        sender: "freelancer",
        type: "text"
      },
      unreadCount: 1,
      pinned: false,
      messages: [
        {
          id: 1,
          content: "Bonjour ! J'ai quelques questions sur les données à analyser. Pouvez-vous m'éclairer ?",
          timestamp: "2024-01-13T14:20:00",
          sender: "freelancer",
          type: "text"
        }
      ]
    }
  ];

  const currentConversation = conversations.find(conv => conv.id === selectedConversation);
  
  const filteredConversations = conversations.filter(conv =>
    conv.freelancer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.project.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch(status) {
      case 'online': return 'bg-green-400';
      case 'offline': return 'bg-gray-400';
      case 'away': return 'bg-yellow-400';
      default: return 'bg-gray-400';
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    }
  };

  const handleSendMessage = () => {
    if (messageText.trim()) {
      // Logic to send message
      console.log('Sending message:', messageText);
      setMessageText('');
    }
  };

  const ConversationItem = ({ conversation }) => (
    <div
      onClick={() => setSelectedConversation(conversation.id)}
      className={`p-4 cursor-pointer transition-all duration-200 border-l-4 ${
        selectedConversation === conversation.id
          ? 'bg-green-50 border-green-500'
          : 'hover:bg-gray-50 border-transparent'
      }`}
    >
      <div className="flex items-start space-x-3">
        <div className="relative">
          <div className={`w-12 h-12 bg-gradient-to-r ${conversation.freelancer.gradient} rounded-xl flex items-center justify-center text-white font-bold shadow-lg`}>
            {conversation.freelancer.avatar}
          </div>
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 ${getStatusColor(conversation.freelancer.status)} rounded-full border-2 border-white`}></div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-gray-900 truncate">{conversation.freelancer.name}</h3>
              {conversation.pinned && <Pin className="w-4 h-4 text-yellow-500" />}
            </div>
            <div className="flex items-center space-x-2">
              {conversation.unreadCount > 0 && (
                <span className="w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {conversation.unreadCount}
                </span>
              )}
              <span className="text-xs text-gray-500">{formatTime(conversation.lastMessage.timestamp)}</span>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 mb-1">{conversation.project.title}</p>
          <p className="text-sm text-gray-500 truncate">{conversation.lastMessage.content}</p>
        </div>
      </div>
    </div>
  );

  const MessageBubble = ({ message, isFromCompany }) => (
    <div className={`flex ${isFromCompany ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
        isFromCompany 
          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
          : 'bg-gray-100 text-gray-900'
      }`}>
        {message.type === 'text' && (
          <p className="text-sm">{message.content}</p>
        )}
        
        {message.type === 'file' && (
          <div className="flex items-center space-x-3">
            <FileText className={`w-8 h-8 ${isFromCompany ? 'text-white' : 'text-gray-500'}`} />
            <div>
              <p className="text-sm font-medium">{message.fileName}</p>
              <p className={`text-xs ${isFromCompany ? 'text-green-100' : 'text-gray-500'}`}>
                {message.fileSize}
              </p>
            </div>
            <Download className={`w-4 h-4 ${isFromCompany ? 'text-white' : 'text-gray-500'} cursor-pointer hover:scale-110 transition-transform`} />
          </div>
        )}
        
        {message.type === 'image' && (
          <div className="space-y-2">
            <div className="w-48 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
              <Image className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-xs">{message.fileName}</p>
          </div>
        )}
        
        <p className={`text-xs mt-1 ${isFromCompany ? 'text-green-100' : 'text-gray-500'}`}>
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Conversations List */}
      <div className="w-1/3 border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <MessageSquare className="w-6 h-6 mr-2 text-green-500" />
              Messages
            </h1>
            <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
              <Plus className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Quick Filters */}
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="flex space-x-2">
            <button className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-lg">
              Tous
            </button>
            <button className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors">
              Non lus
            </button>
            <button className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors">
              Épinglés
            </button>
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.map((conversation) => (
            <ConversationItem key={conversation.id} conversation={conversation} />
          ))}
        </div>
      </div>

      {/* Chat Area */}
      {currentConversation ? (
        <div className="flex-1 flex flex-col">
          {/* Chat Header */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className={`w-12 h-12 bg-gradient-to-r ${currentConversation.freelancer.gradient} rounded-xl flex items-center justify-center text-white font-bold shadow-lg`}>
                    {currentConversation.freelancer.avatar}
                  </div>
                  <div className={`absolute -bottom-1 -right-1 w-4 h-4 ${getStatusColor(currentConversation.freelancer.status)} rounded-full border-2 border-white`}></div>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{currentConversation.freelancer.name}</h3>
                  <p className="text-sm text-gray-600">{currentConversation.freelancer.title}</p>
                  <p className="text-xs text-gray-500">{currentConversation.freelancer.lastSeen}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 px-3 py-1 bg-white rounded-lg border border-gray-200">
                  <Briefcase className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{currentConversation.project.title}</span>
                </div>
                <button className="p-2 hover:bg-white rounded-lg transition-colors duration-200">
                  <Phone className="w-5 h-5 text-gray-600" />
                </button>
                <button className="p-2 hover:bg-white rounded-lg transition-colors duration-200">
                  <Video className="w-5 h-5 text-gray-600" />
                </button>
                <button className="p-2 hover:bg-white rounded-lg transition-colors duration-200">
                  <MoreVertical className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            </div>
          </div>

          {/* Trust Indicator */}
          <div className="px-6 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-700 font-medium">Freelance Certifié</span>
              </div>
              <div className="flex items-center space-x-2">
                <Heart className="w-4 h-4 text-red-500" />
                <span className="text-sm text-gray-600">Valeurs alignées</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-sm text-gray-600">4.9/5 (127 avis)</span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-6 overflow-y-auto bg-gray-50">
            {currentConversation.messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                isFromCompany={message.sender === 'company'}
              />
            ))}
          </div>

          {/* Message Input */}
          <div className="p-6 border-t border-gray-200 bg-white">
            <div className="flex items-end space-x-4">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                    <Paperclip className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                    <Image className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                    <Calendar className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                <div className="relative">
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Tapez votre message..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none text-sm"
                    rows="3"
                  />
                  <button className="absolute bottom-3 right-3 p-1 hover:bg-gray-100 rounded-lg transition-colors duration-200">
                    <Smile className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
              
              <button
                onClick={handleSendMessage}
                disabled={!messageText.trim()}
                className={`p-3 rounded-xl font-semibold transition-all duration-200 ${
                  messageText.trim()
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
              <span>Appuyez sur Entrée pour envoyer, Shift+Entrée pour une nouvelle ligne</span>
              <div className="flex items-center space-x-4">
                <span>Chiffrement end-to-end activé</span>
                <Shield className="w-4 h-4 text-green-500" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sélectionnez une conversation</h3>
            <p className="text-gray-600">Choisissez une conversation pour commencer à échanger avec vos freelances.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;